import { useMemo, useState } from "react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { Badge } from "@/components/ui/badge"
import { Progress } from "@/components/ui/progress"
import { Search, SortAsc, Filter, Eye, ClipboardList, PackagePlus, RefreshCcw, CheckCircle, Download, X } from "lucide-react"
import { Link } from "@tanstack/react-router"
import { inventoryKpis, allInventory, recentMovement, type InventoryItem } from "../-data/fixtures"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { toast } from "@/components/ui/use-toast"
import { AutoComplete, type Option } from "@/components/ui/autocomplete"

export default function Screen() {
  const [query, setQuery] = useState("")
  const [sortBy, setSortBy] = useState<"name" | "expiration" | "stockStatus">("name")
  const [dir, setDir] = useState<"asc" | "desc">("asc")
  const [tab, setTab] = useState<"overview" | "list">("overview")
  const [uppOnly, setUppOnly] = useState(false)

  const filtered = useMemo(() => filterAndSort(allInventory, query, sortBy, dir, uppOnly), [query, sortBy, dir, uppOnly])

  return (
    <div className="h-full overflow-y-auto mt-4 pb-12">
      <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
        <div className="flex items-center justify-between mb-3 sticky top-0 bg-white z-10 py-2">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="list">Inventory List</TabsTrigger>
          </TabsList>
          {tab === "list" && (
            <div className="relative w-[320px] max-w-full">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search items…" className="pl-8 rounded-md border h-8" />
            </div>
          )}
        </div>

        <TabsContent value="overview">
          <OverviewTab />
        </TabsContent>

        <TabsContent value="list">
          <ListTab
            query={query}
            setQuery={setQuery}
            sortBy={sortBy}
            setSortBy={setSortBy}
            dir={dir}
            setDir={setDir}
            items={filtered}
            uppOnly={uppOnly}
            setUppOnly={setUppOnly}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}

function OverviewTab() {
  const [query, setQuery] = useState("")
  const [sortBy, setSortBy] = useState<"name" | "expiration" | "stockStatus">("name")
  const [dir, setDir] = useState<"asc" | "desc">("asc")
  
  // Use filterAndSort for overview table as well
  const items = useMemo(() => filterAndSort(recentMovement, query, sortBy, dir, false), [query, sortBy, dir])

  const sortLabels = {
    name: "Name",
    expiration: "Expiration Date",
    stockStatus: "Stock Status"
  }

  const handleSort = (key: "name" | "expiration" | "stockStatus") => {
    if (sortBy === key) {
      setDir(dir === "asc" ? "desc" : "asc")
    } else {
      setSortBy(key)
      setDir("asc")
    }
  }

  return (
    <div className="space-y-3">
      <hr className="my-1" />
      <div className="grid grid-cols-4 gap-0 divide-x divide-gray-200">
        <KpiItem label="Total Medications" value={inventoryKpis.totalMedications} />
        <KpiItem label="Restock Time" value={`${inventoryKpis.restockTimeHours} hrs`} />
        <KpiItem label="Refilled Meds" value={inventoryKpis.refilledMeds} />
        <KpiItem label="Inventory Accuracy" value={`${inventoryKpis.inventoryAccuracyPct}%`} />
      </div>
      <hr className="my-1" />
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">Recent Movement</h2>
          </div>
          <div className="flex items-center gap-2 w-[420px] max-w-full">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input 
                value={query} 
                onChange={(e) => setQuery(e.target.value)} 
                placeholder="Search items…" 
                className="pl-8 pr-8 rounded-md border h-8" 
              />
            </div>
            <Button variant="outline" size="xs" className="h-8 px-2" disabled>
              <Download className="h-4 w-4" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="xs" className="h-8">
                  <SortAsc className="h-3 w-3 mr-1" />
                  Sort: {sortLabels[sortBy]} {dir === "asc" ? "↑" : "↓"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Sort Options</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleSort("name")}>
                  Name {sortBy === "name" && (dir === "asc" ? "↑" : "↓")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleSort("expiration")}>
                  Expiration Date {sortBy === "expiration" && (dir === "asc" ? "↑" : "↓")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleSort("stockStatus")}>
                  Stock Status {sortBy === "stockStatus" && (dir === "asc" ? "↑" : "↓")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <InventoryTable items={items} />
      </div>
    </div>
  )
}

function ListTab({
  query,
  setQuery,
  sortBy,
  setSortBy,
  dir,
  setDir,
  items,
  uppOnly,
  setUppOnly,
}: {
  query: string
  setQuery: (v: string) => void
  sortBy: "name" | "expiration" | "stockStatus"
  setSortBy: (v: "name" | "expiration" | "stockStatus") => void
  dir: "asc" | "desc"
  setDir: (v: "asc" | "desc") => void
  items: InventoryItem[]
  uppOnly: boolean
  setUppOnly: (v: boolean) => void
}) {
  const [isRequisitionOpen, setIsRequisitionOpen] = useState(false)
  const [isRequestStockOpen, setIsRequestStockOpen] = useState(false)
  const [isUpdateStatusOpen, setIsUpdateStatusOpen] = useState(false)

  const [reqItemInput, setReqItemInput] = useState("")
  const [reqItemValue, setReqItemValue] = useState<Option>()
  
  const [stockItemInput, setStockItemInput] = useState("")
  const [stockItemValue, setStockItemValue] = useState<Option>()
  
  const [statusItemInput, setStatusItemInput] = useState("")
  const [statusItemValue, setStatusItemValue] = useState<Option>()

  const inventoryOptions = useMemo(() => {
    return allInventory.map((item) => ({
      value: item.id,
      label: item.name,
    }))
  }, [])

  // Labels mapping for sort options
  const sortLabels = {
    name: "Name",
    expiration: "Expiration Date",
    stockStatus: "Stock Status"
  }

  const handleSort = (key: "name" | "expiration" | "stockStatus") => {
    if (sortBy === key) {
      setDir(dir === "asc" ? "desc" : "asc")
    } else {
      setSortBy(key)
      setDir("asc")
    }
  }

  const handleCreateRequisition = () => {
    setIsRequisitionOpen(false)
    toast({
      variant: "success",
      title: "Requisition Created",
      description: "Your requisition has been successfully created.",
    })
  }

  const handleRequestStock = () => {
    setIsRequestStockOpen(false)
    toast({
      variant: "success",
      title: "Stock Requested",
      description: "Stock request has been sent to the warehouse.",
    })
  }

  const handleUpdateStatus = () => {
    setIsUpdateStatusOpen(false)
    toast({
      variant: "success",
      title: "Status Updated",
      description: "Item status has been updated successfully.",
    })
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">All Inventory</h2>
          <p className="text-xs text-muted-foreground">Items available in your inventory</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="xs" onClick={() => setIsRequisitionOpen(true)}>
            <ClipboardList className="h-3.5 w-3.5 mr-1" />
            Create Requisition
          </Button>
          <Button variant="outline" size="xs" onClick={() => setIsRequestStockOpen(true)}>
            <RefreshCcw className="h-3.5 w-3.5 mr-1" />
            Request Stock
          </Button>
          <Button variant="outline" size="xs" onClick={() => setIsUpdateStatusOpen(true)}>
            <CheckCircle className="h-3.5 w-3.5 mr-1" />
            Update Status
          </Button>
        </div>
      </div>
      <hr className="my-1" />
      <div className="flex items-center justify-between gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <div className="border rounded-md bg-white px-3 py-1 flex items-center gap-2 text-xs whitespace-nowrap cursor-pointer hover:bg-gray-50">
              <Filter className="h-3.5 w-3.5" />
              <span className="text-muted-foreground">Sorted by</span>
              <span className="font-medium">{sortLabels[sortBy]} {dir === "asc" ? "↑" : "↓"}</span>
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuLabel>Sort Options</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleSort("name")}>
              Name {sortBy === "name" && (dir === "asc" ? "↑" : "↓")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleSort("expiration")}>
              Expiration Date {sortBy === "expiration" && (dir === "asc" ? "↑" : "↓")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleSort("stockStatus")}>
              Stock Status {sortBy === "stockStatus" && (dir === "asc" ? "↑" : "↓")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        
        <div 
          className={`border rounded-md px-3 py-1 flex items-center gap-2 text-xs whitespace-nowrap cursor-pointer transition-colors ${uppOnly ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-white hover:bg-gray-50'}`}
          onClick={() => setUppOnly(!uppOnly)}
        >
          <Eye className="h-3.5 w-3.5" />
          <span className={uppOnly ? "text-blue-700" : "text-muted-foreground"}>Show UPP only</span>
          <span className="font-medium">{items.length}</span>
        </div>
      </div>
      <hr className="my-1" />
      <InventoryTable items={items} />

      {/* Create Requisition Sheet */}
      <Sheet open={isRequisitionOpen} onOpenChange={setIsRequisitionOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Create Requisition</SheetTitle>
            <SheetDescription>
              Create a new requisition for inventory items.
            </SheetDescription>
          </SheetHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="req-item">Item</Label>
              <AutoComplete
                options={inventoryOptions}
                emptyMessage="No item found."
                placeholder="Select item"
                input={reqItemInput}
                onInputChanged={setReqItemInput}
                value={reqItemValue}
                onValueChange={setReqItemValue}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="req-qty">Quantity</Label>
              <Input id="req-qty" type="number" placeholder="Enter quantity" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="req-priority">Priority</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="req-notes">Notes</Label>
              <Textarea id="req-notes" placeholder="Additional notes..." />
            </div>
          </div>
          <SheetFooter>
            <Button onClick={handleCreateRequisition}>Create Requisition</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Request Stock Sheet */}
      <Sheet open={isRequestStockOpen} onOpenChange={setIsRequestStockOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Request Stock</SheetTitle>
            <SheetDescription>
              Request additional stock from the central warehouse.
            </SheetDescription>
          </SheetHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="stock-item">Item</Label>
              <AutoComplete
                options={inventoryOptions}
                emptyMessage="No item found."
                placeholder="Select item"
                input={stockItemInput}
                onInputChanged={setStockItemInput}
                value={stockItemValue}
                onValueChange={setStockItemValue}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="stock-qty">Quantity Needed</Label>
              <Input id="stock-qty" type="number" placeholder="Enter quantity" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="stock-reason">Reason</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low Stock</SelectItem>
                  <SelectItem value="expired">Expired Stock</SelectItem>
                  <SelectItem value="damaged">Damaged Goods</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <SheetFooter>
            <Button onClick={handleRequestStock}>Submit Request</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Update Status Sheet */}
      <Sheet open={isUpdateStatusOpen} onOpenChange={setIsUpdateStatusOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Update Status</SheetTitle>
            <SheetDescription>
              Update the status of an inventory item.
            </SheetDescription>
          </SheetHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="status-item">Item</Label>
              <AutoComplete
                options={inventoryOptions}
                emptyMessage="No item found."
                placeholder="Select item"
                input={statusItemInput}
                onInputChanged={setStatusItemInput}
                value={statusItemValue}
                onValueChange={setStatusItemValue}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="status-val">New Status</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="discontinued">Discontinued</SelectItem>
                  <SelectItem value="review">Under Review</SelectItem>
                </SelectContent>
              </Select>
            </div>
             <div className="grid gap-2">
              <Label htmlFor="status-notes">Notes</Label>
              <Textarea id="status-notes" placeholder="Reason for status change..." />
            </div>
          </div>
          <SheetFooter>
            <Button onClick={handleUpdateStatus}>Update Status</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  )
}

function InventoryTable({ items }: { items: InventoryItem[] }) {
  return (
    <Table className="text-sm">
      <TableHeader>
        <TableRow>
          <TableHead className="whitespace-nowrap">Item Name</TableHead>
          <TableHead className="whitespace-nowrap">Stock Status</TableHead>
          <TableHead className="whitespace-nowrap">Availability</TableHead>
          <TableHead className="whitespace-nowrap">Unit Price to Public</TableHead>
          <TableHead className="whitespace-nowrap">UPP Status</TableHead>
          <TableHead className="whitespace-nowrap">Dispense Mode</TableHead>
          <TableHead className="whitespace-nowrap">Expiration</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((i) => (
          <TableRow key={i.id} className="cursor-pointer">
            <TableCell>
              <div className="flex flex-col">
                <Link
                  to="/inventory/$itemId"
                  params={{ itemId: i.id }}
                  className="font-medium text-black no-underline hover:underline hover:text-blue-700"
                >
                  {i.name}
                </Link>
                <span className="text-xs text-muted-foreground">{i.category}</span>
              </div>
            </TableCell>
            <TableCell>
              <span className="text-sm font-medium">{i.stockStatus}%</span>
            </TableCell>
            <TableCell>
              {i.availability === "In Stock" ? (
                <Badge className="bg-green-600 text-white border-transparent hover:bg-green-700">In Stock</Badge>
              ) : (
                <Badge variant="destructive">Out of Stock</Badge>
              )}
            </TableCell>
            <TableCell>AED {i.unitPrice}</TableCell>
            <TableCell>{i.uppStatus}</TableCell>
            <TableCell>{i.dispenseMode}</TableCell>
            <TableCell>{i.expiration}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function KpiItem({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="p-4 relative flex-1">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold">{typeof value === "number" ? value.toLocaleString() : value}</div>
    </div>
  )
}

function filterAndSort(list: InventoryItem[], q: string, sortBy: "name" | "expiration" | "stockStatus", dir: "asc" | "desc", uppOnly: boolean) {
  const query = q.trim().toLowerCase()
  let res = list
  
  if (query) {
    res = res.filter((i) => i.name.toLowerCase().includes(query) || i.category.toLowerCase().includes(query))
  }

  // Filter for UPP if enabled
  if (uppOnly) {
     res = res.filter(i => i.uppStatus === "Active")
  }

  res = res.slice().sort((a, b) => {
    const av = sortBy === "name" ? a.name : sortBy === "expiration" ? a.expiration : a.stockStatus.toString()
    const bv = sortBy === "name" ? b.name : sortBy === "expiration" ? b.expiration : b.stockStatus.toString()
    
    // For numbers like stockStatus, we should compare numerically
    if (sortBy === "stockStatus") {
        return dir === "asc" ? a.stockStatus - b.stockStatus : b.stockStatus - a.stockStatus
    }
    
    return dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av)
  })
  return res
}
